import React, { useCallback, useContext, useMemo } from 'react';
import CopilotChatMessages from '@components/Copilot/ChatMessages/CopilotChatMessages';
import { COPILOT_PANEL_WIDTH, copilotConfig, defaultSessionData } from '@components/Copilot/constants';
import CopilotDisclaimer from '@components/Copilot/CopilotDisclaimer';
import CopilotHeader from '@components/Copilot/CopilotHeader';
import CopilotNavigationBlocker from '@components/Copilot/CopilotNavigationBlocker';
import CopilotSendFeedback from '@components/Copilot/CopilotSendFeedback';
import DefaultPrompts from '@components/Copilot/DefaultPrompts';
import { useInitChatbotSession } from '@components/Copilot/hooks';
import PromptInput from '@components/Copilot/PromptInput';
import { AgentId, ChatbotErrorType, CopilotChatMessageType, TCopilotError } from '@components/Copilot/types';
import { AvButton } from '@fabric-ui/components';
import { Illustration as ErrorIcon } from '@fabric-ui/icons';
import { APPBAR_HEIGHT, flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Divider, Drawer, drawerClasses } from '@mui/material';
import { useAvContext } from '../../context/AvContextProvider';
import { useAvCopilotContext } from '../../context/AvCopilotProvider';
import { LayoutContext } from '../../context/LayoutContext';
import { FeatureFlags } from '../../types';

const AvCopilot = () => {
  const { sideBar: sidebarWidth } = useContext(LayoutContext).layoutWidths;
  const { user, featureFlags } = useAvContext();
  const {
    isOpen,
    closeCopilotWithBlocker,
    addMessage,
    lastSessionProps,
    sessionId,
    setThreadId,
    messages,
    postMessage,
    publishPrompt,
    isPollingEnabled,
    restartCopilotSession,
    isDirty,
    isNavigationBlockerOpen,
    setIsNavigationBlockerOpen,
    forceDiscardCopilot,
    conversationErrorMessage,
    setShowActionButtons,
  } = useAvCopilotContext();

  const {
    data: sessionData,
    isError,
    isLoading,
    isRefetching: isSessionReinitializing,
  } = useInitChatbotSession({
    onSuccess: ({ data }) => {
      if (data) {
        setThreadId(data.chatRunInfo.threadId);
      }
    },
    variables: lastSessionProps.agentId === AgentId.TICKET_AGENT ? (lastSessionProps.additionalParams as any) : undefined,
    enabled: isOpen && lastSessionProps.agentId === AgentId.TICKET_AGENT,
  });

  const isNewImplSessionInitializing = isPollingEnabled && !messages.length;
  const isOldImplSessionInitializing = isLoading || isSessionReinitializing;
  const isSessionInitializing =
    lastSessionProps.agentId === AgentId.TICKET_AGENT ? isOldImplSessionInitializing : isNewImplSessionInitializing;

  const { defaultPrompts, chatRunInfo, useFallbackQueries } = sessionData?.data ?? defaultSessionData;

  const sessionError: TCopilotError | null = useMemo(
    () =>
      conversationErrorMessage ||
      sessionData?.error ||
      (isError && {
        message: 'Something went wrong. Please try again.',
        type: ChatbotErrorType.INTERNAL,
      }) ||
      null,
    [sessionData?.error, isError, conversationErrorMessage]
  );

  const firstName = user?.userName?.split(' ')[0];

  const isPromptInputDisabled = useMemo(
    () => isSessionInitializing || isPollingEnabled || !!sessionError,
    [isSessionInitializing, sessionError, isPollingEnabled]
  );

  const onUserPromptHandler = useCallback(
    async (prompt: string) => {
      addMessage({
        content: prompt,
        timestamp: new Date().toISOString(),
        type: CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_USER,
      });
      if (lastSessionProps.agentId === AgentId.TICKET_AGENT) {
        publishPrompt({ chatRunInfo, userPrompt: prompt });
      } else {
        postMessage({ sessionId, message: prompt });
      }
      setShowActionButtons(true);
    },
    [addMessage, postMessage, sessionId, publishPrompt, chatRunInfo, lastSessionProps.agentId]
  );

  const onErrorHandler = () => {
    restartCopilotSession();
  };

  return (
    <Box sx={{ gridArea: 'copilot', width: isOpen ? 'fit-content' : 0 }}>
      <CopilotNavigationBlocker
        isDirty={isDirty}
        isManualDialogOpen={isNavigationBlockerOpen}
        onManualClose={() => setIsNavigationBlockerOpen(false)}
        onManualDiscard={() => {
          setIsNavigationBlockerOpen(false);
          forceDiscardCopilot();
        }}
      />
      <Drawer
        disablePortal
        anchor="right"
        open={isOpen}
        disableEnforceFocus
        disableRestoreFocus
        onClose={closeCopilotWithBlocker}
        hideBackdrop
        sx={{
          zIndex: theme => theme.zIndex.drawer + 5,
          height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
          [`&, .${drawerClasses.paper}`]: {
            position: 'relative',
          },
        }}>
        <Box
          sx={{
            ...flex.col,
            width: COPILOT_PANEL_WIDTH,
            position: 'relative',
            height: '100%',
            background: 'linear-gradient(180deg, #FFF 50.35%, #F2F7FF 100%)',
            maxWidth: `calc(100vw - ${sidebarWidth}px)`,
          }}>
          <Box component="header" sx={{ flexShrink: 0 }}>
            <CopilotHeader onClose={closeCopilotWithBlocker} isReducedContextMode={useFallbackQueries} />
            <Divider />
          </Box>

          <Box
            sx={{
              ...flex.justifyCenterStart,
              position: 'relative',
              overflowY: 'auto',
              flexGrow: 1,
              width: '100%',
              maxWidth: '100%',
              px: 3,
              pt: messages.length && !sessionError ? 0 : '30%',
              pb: 2,
            }}>
            {sessionError && !isSessionInitializing ? (
              <ErrorMessage {...sessionError} onClick={onErrorHandler} />
            ) : messages.length ? (
              <CopilotChatMessages
                messages={messages}
                isLoading={isPollingEnabled}
                onUserPromptHandler={onUserPromptHandler}
                defaultPrompts={defaultPrompts}
              />
            ) : (
              <DefaultPrompts
                firstName={firstName}
                prompts={copilotConfig[lastSessionProps.agentId]?.defaultPrompts || defaultPrompts || []}
                onClick={onUserPromptHandler}
                isLoading={isSessionInitializing}
                agentId={lastSessionProps.agentId}
              />
            )}
          </Box>

          <Box sx={{ ...flex.col, gap: 1.5, flexShrink: 0, px: 3, pb: 2 }}>
            <PromptInput onSend={onUserPromptHandler} disabled={isPromptInputDisabled} />
            <Box sx={{ ...flex.center, gap: 1 }}>
              <CopilotDisclaimer />
              <Divider
                orientation="vertical"
                variant="middle"
                sx={{
                  height: 12,
                  borderRightWidth: 1.5,
                }}
                flexItem
              />
              {featureFlags[FeatureFlags.salesforceSsoAvailable] && <CopilotSendFeedback />}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

const ErrorMessage = ({ message = 'Something went wrong. Please try again.', onClick }: { message?: string; onClick: () => void }) => (
  <Box sx={{ ...flex.colItemsCenter, gap: 0.5 }}>
    <ErrorIcon style={iconSize(70)} />
    <Box
      sx={{
        fontSize: 16,
        fontWeight: 600,
        color: theme => theme.palette.colors.neutrals[800],
      }}>
      Error Loading
    </Box>
    <Box sx={{ textAlign: 'center', mb: 1, color: theme => theme.palette.colors.neutrals[600] }}>{message}</Box>
    <AvButton
      size="xSmall"
      variant="outlined"
      aria-label="Retry"
      onClick={onClick}
      sx={{ color: theme => theme.palette.colors.neutrals[700] }}>
      Retry
    </AvButton>
  </Box>
);

export default AvCopilot;
